import { useState, useRef, useEffect } from 'preact/hooks';
import { renderSafeMarkdown } from '../lib/safeMarkdown';
import { CardPolicyNotice } from '../components/CardPolicyNotice';
import { QuickAnswers } from '../components/QuickAnswers';
import { startOwnerTurn, pendingOwnerTurn, recoverOwnerTurn, clearOwnerTurn, OwnerTurnError } from '../api/ownerTurns.js';
import { ASSISTANT_INITIAL, ASSISTANT_NAME } from '../brand.js';

const API_BASE = '';

export function ChatView({ dashboardData, knowledgeData, onDataRefresh, refineContext, onRefineContextConsumed, onComplete, onClose }) {
  const { initial_message, salon_name, bootstrap, onboarding_chat_complete, onboarding_session_id } = dashboardData;
  const recentSessions = knowledgeData?.recent_sessions || [];

  const [messages, setMessages] = useState([]);
  const [cardReviewRequired, setCardReviewRequired] = useState(dashboardData.card_policy_review_required === true);
  useEffect(() => setCardReviewRequired(dashboardData.card_policy_review_required === true), [dashboardData.card_policy_review_required]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [activeContext, setActiveContext] = useState({ type: 'general', id: null });
  const [sessionId, setSessionId] = useState(null);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [onboardingRestored, setOnboardingRestored] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const hasShownInitialGreeting = useRef(false);
  const isFirstLoad = useRef(true);

  const recoveryScope = dashboardData.chat_recovery_scope;
  const pendingRef = useRef(null);
  const operationRef = useRef(null);
  const [recoveryError, setRecoveryError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    operationRef.current?.abort();
    operationRef.current = controller;
    setMessages([]);
    setCardReviewRequired(dashboardData.card_policy_review_required === true);
    setSessionId(null);
    setSessionEnded(false);
    setIsLoading(false);
    setInputText('');
    setOnboardingRestored(false);
    setShowHistory(false);
    isFirstLoad.current = true;
    setCurrentQuestion(null);
    setActiveContext({ type: 'general', id: null });
    hasShownInitialGreeting.current = false;
    setRecoveryError(null);
    try {
      pendingRef.current = recoveryScope ? pendingOwnerTurn(recoveryScope) : null;
      if (pendingRef.current) runTurn(null);
    } catch (error) {
      setRecoveryError({ message: error.message });
    }
    return () => { operationRef.current?.abort(); pendingRef.current = null; };
  }, [recoveryScope]);

  useEffect(() => {
    if (!pendingRef.current && !onboarding_chat_complete && onboarding_session_id && !onboardingRestored && !refineContext) {
      setOnboardingRestored(true);
      restoreSessionMessages({ session_id: onboarding_session_id }, operationRef.current?.signal);
    }
  }, [onboarding_chat_complete, onboarding_session_id, onboardingRestored, refineContext]);

  useEffect(() => {
    if (refineContext && !pendingRef.current) {
      if (refineContext.restoreSession) {
        restoreSessionMessages(refineContext.restoreSession, operationRef.current?.signal);
        onRefineContextConsumed?.();
        return;
      }

      setActiveContext({
        type: refineContext.type,
        id: refineContext.item?.id || null,
      });

      const framingMessage = buildFramingMessage(refineContext);
      if (framingMessage) {
        setMessages([{
          id: `framing-${Date.now()}`,
          from: 'jonathan',
          text: framingMessage,
        }]);
      }

      setTimeout(() => inputRef.current?.focus(), 100);
      onRefineContextConsumed?.();
    }
  }, [refineContext]);

  async function restoreSessionMessages(session, signal, manageBusy = true) {
    try {
      if (manageBusy) setIsLoading(true);
      const response = await fetch(`${API_BASE}/v1/owner/chat?session_id=${encodeURIComponent(session.session_id)}`, {
        signal,
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load conversation');
      }

      const data = await response.json();
      signal?.throwIfAborted();
      if (typeof data.card_policy_review_required === 'boolean') setCardReviewRequired(data.card_policy_review_required);
      const restoredMessages = data.messages.map((msg, i) => ({
        id: `restored-${i}`,
        from: msg.role === 'user' ? 'owner' : 'jonathan',
        text: msg.content,
        contextType: msg.context_type,
        contextId: msg.context_id,
        messageType: msg.message_type,
        learned: msg.learned || [],
      }));

      setMessages(restoredMessages);
      setSessionId(data.session_id || session.session_id);
      setCurrentQuestion(data.current_question || null);
      const lastContext = [...(data.messages || [])].reverse().find((msg) => msg.context_type);
      if (lastContext) {
        setActiveContext({
          type: lastContext.context_type || 'general',
          id: lastContext.context_id || null,
        });
      } else {
        setActiveContext({ type: 'general', id: null });
      }
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (err) {
      if (!manageBusy) throw err;
      if (signal?.aborted) return;
      console.error('Failed to restore session:', err);
      setMessages([{
        id: 'error-restore',
        from: 'jonathan',
        text: 'I couldn\'t load that conversation. Please try opening it again.',
      }]);
    } finally {
      if (manageBusy && !signal?.aborted) setIsLoading(false);
    }
  }

  function buildFramingMessage(context) {
    const item = context.item;
    const atoms = item?.atoms || {};
    const name = item?.display_name || item?.name || context.topic;

    if (context.type === 'team' && item) {
      const specialties = atoms.specializations?.slice(0, 3).join(', ') || 'various services';
      const schedule = atoms.availability_pattern || '';
      let msg = `Let's talk about ${name}. `;
      if (specialties !== 'various services') msg += `I know they specialize in ${specialties}. `;
      if (schedule) msg += `They work ${schedule}. `;
      msg += `What would you like to add or change?`;
      return msg;
    }

    if (context.type === 'service' && item) {
      const duration = item.duration_minutes ? `${item.duration_minutes} minutes` : '';
      const price = item.price_money ? `$${(item.price_money.amount / 100).toFixed(0)}` : '';
      let msg = `Let's refine ${name}. `;
      if (duration || price) {
        msg += `Currently set at ${[duration, price].filter(Boolean).join(', ')}. `;
      }
      msg += `What should I know about this service?`;
      return msg;
    }

    if (context.type === 'policy') {
      return `Let's talk about your booking policies. What would you like to set up or change about cancellations, deposits, or scheduling rules?`;
    }

    if (context.type === 'profile') {
      return `Let's refine how I communicate with your clients. What tone or style fits your salon best?`;
    }

    if (name) {
      return `Let's talk about ${name}. What would you like me to know?`;
    }

    return `What would you like to teach me?`;
  }

  useEffect(() => {
    if (pendingRef.current || messages.length > 0) return;
    if (refineContext) return;
    if (hasShownInitialGreeting.current) return;

    const shouldSkipForOnboardingRestore =
      isFirstLoad.current &&
      !onboarding_chat_complete &&
      onboarding_session_id;

    if (shouldSkipForOnboardingRestore) {
      isFirstLoad.current = false;
      return;
    }

    isFirstLoad.current = false;

    if (initial_message) {
      hasShownInitialGreeting.current = true;

      const introMessage = {
        id: 'intro-1',
        from: 'jonathan',
        text: initial_message.greeting,
        observations: initial_message.observations,
      };

      if (initial_message.question) {
        const questionMessage = {
          id: 'intro-2',
          from: 'jonathan',
          text: initial_message.question.text,
        };
        setMessages([introMessage, questionMessage]);
        setCurrentQuestion(initial_message.question);
        setActiveContext({
          type: initial_message.question.context_type || 'general',
          id: initial_message.question.context_id || null,
        });
      } else if (initial_message.onboarding_complete) {
        const continueMessage = {
          id: 'intro-2',
          from: 'jonathan',
          text: "I'm ready whenever you are. Want to teach me more about how your salon runs?",
        };
        setMessages([introMessage, continueMessage]);
      } else {
        setMessages([introMessage]);
      }
    }
  }, [initial_message, onboarding_chat_complete, onboarding_session_id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading, currentQuestion]);

  function processChatResponse(data) {
    if (!data) return;
    if (typeof data.card_policy_review_required === 'boolean') setCardReviewRequired(data.card_policy_review_required);

    if (data.session_id) {
      setSessionId(data.session_id);
    }

    const learned = Array.isArray(data.learned) ? data.learned : [];
    if (data.response) {
      const jonathanMsg = {
        id: Date.now(),
        from: 'jonathan',
        text: data.response,
        learned,
      };
      setMessages(prev => [...prev, jonathanMsg]);
    }

    // Quick answers from top-level response field
    if (data.quick_answers?.length) {
      setCurrentQuestion({ quick_answers: data.quick_answers });
    } else {
      setCurrentQuestion(null);
    }
  }

  async function runTurn(payload) {
    if (payload && (pendingRef.current || isLoading)) return;
    operationRef.current?.abort();
    const controller = new AbortController();
    operationRef.current = controller;
    setIsLoading(true);
    setRecoveryError(null);
    setCurrentQuestion(null);
    let record;
    try {
      record = payload ? startOwnerTurn(recoveryScope, {
        ...payload, context_type: activeContext.type || 'general',
        context_id: activeContext.id || null, session_id: sessionId,
      }) : pendingOwnerTurn(recoveryScope);
      if (!record) throw new OwnerTurnError('There is no pending reply to recover.');
      pendingRef.current = record;
      setSessionId(record.session_id);
      if (payload) {
        setMessages(prev => [...prev, { id: record.turn_id, from: 'owner',
          text: payload.done_for_now ? 'Done for now' : payload.message }]);
        setInputText('');
      }
      const completed = await recoverOwnerTurn(recoveryScope, {
        signal: controller.signal,
        onAccepted: accepted => { pendingRef.current = accepted; setSessionId(accepted.session_id); },
      });
      controller.signal.throwIfAborted();
      if (payload) processChatResponse(completed.result);
      else await restoreSessionMessages({ session_id: completed.session_id }, controller.signal, false);
      controller.signal.throwIfAborted();
      clearOwnerTurn(recoveryScope, completed.turn_id);
      pendingRef.current = null;
      if (completed.result.done) { setSessionEnded(true); onComplete?.(); }
    } catch (error) {
      if (controller.signal.aborted) return;
      if (error.terminal && record) {
        clearOwnerTurn(recoveryScope, record.turn_id);
        pendingRef.current = null;
      }
      setRecoveryError({
        message: error instanceof OwnerTurnError ? error.message :
          'The connection was interrupted. Recover your reply before sending another message.',
        canRecover: !!pendingRef.current,
        signIn: error.status === 401,
      });
    } finally {
      if (!controller.signal.aborted) setIsLoading(false);
    }
  }

  function handleSend(e) {
    e?.preventDefault();
    const text = inputText.trim();
    if (text) runTurn({ message: text });
  }

  function handleQuickAnswer(answer) {
    runTurn({ message: answer.label, action_id: answer.action_id, answer_label: answer.label });
  }

  function handleDoneForNow() {
    runTurn({ message: '__DONE_FOR_NOW__', done_for_now: true });
  }

  const hasEnoughConversation = messages.filter(m => m.from === 'owner').length >= 2;
  const showDoneButton = hasEnoughConversation && !isLoading && !sessionEnded;



  return (
    <div
      class="view active fixed inset-0"
      style={{ background: 'var(--cream)' }}
    >
      <header
        style={{
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid var(--border-subtle)',
          padding: 'calc(0.875rem + var(--safe-area-top)) 1rem 0.875rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          flexShrink: 0,
        }}
      >
        <div
          class="logo"
          style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            background: 'var(--sage)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '1.25rem',
          }}
        >
          {ASSISTANT_INITIAL.toLowerCase()}
        </div>
        <div style={{ flex: 1 }}>
          <h1
            class="logo"
            style={{ fontSize: '1.0625rem', fontWeight: 700, margin: 0 }}
          >
            {ASSISTANT_NAME}
          </h1>
          <p
            style={{
              fontSize: '0.75rem',
              color: 'var(--sage)',
              margin: 0,
              fontWeight: 500,
            }}
          >
            {salon_name || 'Your Salon'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'var(--cream)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
              }}
              title="Close"
              aria-label="Close"
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          {recentSessions.length > 0 && (
            <button
              onClick={() => setShowHistory(true)}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'var(--cream)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
              }}
              title="Chat History"
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          )}
          {sessionId && messages.length > 0 && (
            <button
              disabled={isLoading || !!pendingRef.current}
              onClick={() => {
                if (isLoading || pendingRef.current) return;
                setSessionId(null);
                setActiveContext({ type: 'general', id: null });
                setCurrentQuestion(null);
                setSessionEnded(false);

                if (initial_message) {
                  const introMessage = {
                    id: `intro-new-${Date.now()}`,
                    from: 'jonathan',
                    text: initial_message.greeting,
                    observations: initial_message.observations,
                  };

                  if (initial_message.question) {
                    const questionMessage = {
                      id: `intro-q-${Date.now()}`,
                      from: 'jonathan',
                      text: initial_message.question.text,
                    };
                    setMessages([introMessage, questionMessage]);
                    setCurrentQuestion(initial_message.question);
                    setActiveContext({
                      type: initial_message.question.context_type || 'general',
                      id: initial_message.question.context_id || null,
                    });
                  } else if (initial_message.onboarding_complete) {
                    setMessages([introMessage, {
                      id: `intro-complete-${Date.now()}`,
                      from: 'jonathan',
                      text: "I've got everything I need to help your clients! You can always teach me more by chatting below.",
                    }]);
                  } else {
                    setMessages([introMessage]);
                  }
                } else {
                  setMessages([]);
                }
              }}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'var(--cream)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
              }}
              title="New Chat"
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
            </button>
          )}
        </div>
      </header>
      <CardPolicyNotice required={cardReviewRequired && !currentQuestion} disabled={isLoading || !!pendingRef.current || dashboardData.ai_available === false}
        onReview={() => handleQuickAnswer({ label: 'Review card requirement', action_id: 'policy.review_card' })} />

      {recoveryError && (
        <div role="alert" style={{ padding: '16px 24px', background: 'var(--cream)', position: 'relative', zIndex: 2 }}>
          <p>{recoveryError.message}</p>
          {recoveryError.signIn ? <a href="/account/login">Sign in</a> : recoveryError.canRecover && (
            <button type="button" disabled={isLoading} onClick={() => runTurn(null)}>Recover reply</button>
          )}
        </div>
      )}

      {showHistory && (
        <HistoryTray
          sessions={recentSessions}
          onSelect={(session) => {
            if (isLoading || pendingRef.current) return;
            restoreSessionMessages(session, operationRef.current?.signal);
            setShowHistory(false);
          }}
          onClose={() => setShowHistory(false)}
        />
      )}

      <div
        class="chat-scroll"
        style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1rem' }}
      >
        <div
          class="chat-messages-wrapper"
          style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
        >
          {messages.map((msg) => (
            <ChatMessage key={msg.id} message={msg} />
          ))}
          {isLoading && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {currentQuestion && !isLoading && (
        <QuickAnswers question={currentQuestion} onSelect={handleQuickAnswer} />
      )}

      <div
        class="chat-input-area"
        style={{
          background: 'white',
          borderTop: '1px solid var(--border-subtle)',
          padding: '0.875rem 1rem calc(90px + env(safe-area-inset-bottom))',
          flexShrink: 0,
        }}
      >
        {showDoneButton && (
          <div style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
            <button
              onClick={handleDoneForNow}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-secondary)',
                fontSize: '0.8125rem',
                cursor: 'pointer',
                padding: '0.5rem 1rem',
                borderRadius: '16px',
                transition: 'background 0.2s ease, color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'var(--cream)';
                e.target.style.color = 'var(--sage)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = 'var(--text-secondary)';
              }}
            >
              Done for now
            </button>
          </div>
        )}
        <form
          onSubmit={handleSend}
          class="chat-input-wrapper"
          style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}
        >
          <div style={{ flex: 1, position: 'relative' }}>
            <input
              ref={inputRef}
              type="text"
              value={inputText}
              onInput={(e) => setInputText(e.target.value)}
              placeholder={`Message ${ASSISTANT_NAME}...`}
              disabled={isLoading || !!pendingRef.current}
              style={{
                width: '100%',
                border: '1px solid var(--border-subtle)',
                borderRadius: '22px',
                padding: '0.75rem 1rem',
                fontSize: '0.9375rem',
                fontFamily: 'inherit',
                background: 'var(--cream)',
                outline: 'none',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--sage)';
                e.target.style.boxShadow = '0 0 0 3px rgba(110,127,79,0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--border-subtle)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          <button
            type="submit"
            disabled={isLoading || !!pendingRef.current || !inputText.trim()}
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              background: 'var(--sage)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              flexShrink: 0,
              boxShadow: '0 4px 12px rgba(110,127,79,0.3)',
              cursor: 'pointer',
              transition: 'transform 0.1s ease, box-shadow 0.1s ease',
              opacity: isLoading || !inputText.trim() ? 0.5 : 1,
            }}
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
}

function ChatMessage({ message }) {
  const isJonathan = message.from === 'jonathan';
  const observations = message.observations || [];

  const content = isJonathan ? renderSafeMarkdown(message.text) : message.text;

  return (
    <div
      style={{
        display: 'flex',
        gap: '0.625rem',
        maxWidth: '88%',
        marginLeft: isJonathan ? 0 : 'auto',
        flexDirection: isJonathan ? 'row' : 'row-reverse',
      }}
    >
      <div
        class={isJonathan ? 'logo' : ''}
        style={{
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          background: isJonathan ? 'var(--sage)' : 'var(--cream-light)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: isJonathan ? 'white' : 'var(--text-muted)',
          fontSize: isJonathan ? '0.75rem' : '0.6875rem',
          fontWeight: isJonathan ? 400 : 600,
        }}
      >
        {isJonathan ? ASSISTANT_INITIAL.toLowerCase() : 'You'}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {isJonathan && observations.length > 0 && (
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontStyle: 'italic', paddingLeft: '0.25rem' }}>
            {observations.map((obs, i) => (
              <div key={i}>{obs}</div>
            ))}
          </div>
        )}

        <div
          class={isJonathan ? 'bubble-jonathan' : 'bubble-owner'}
          style={{
            background: isJonathan ? 'white' : 'var(--sage)',
            borderRadius: '18px',
            padding: '0.875rem 1rem',
            boxShadow: isJonathan ? 'var(--shadow-sm)' : 'none',
            border: isJonathan ? '1px solid var(--border-subtle)' : 'none',
            color: isJonathan ? 'var(--black)' : 'white',
          }}
        >
          {isJonathan ? (
            <div
              class="chat-markdown"
              style={{ fontSize: '0.9375rem', lineHeight: 1.5 }}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          ) : (
            <p style={{ fontSize: '0.9375rem', margin: 0, lineHeight: 1.5 }}>
              {message.text}
            </p>
          )}
        </div>

      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div style={{ display: 'flex', gap: '0.625rem', maxWidth: '88%' }}>
      <div
        class="logo"
        style={{
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          background: 'var(--sage)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '0.75rem',
        }}
      >
        {ASSISTANT_INITIAL.toLowerCase()}
      </div>
      <div
        style={{
          background: 'white',
          borderRadius: '18px',
          padding: '0.875rem 1rem',
          boxShadow: 'var(--shadow-sm)',
          border: '1px solid var(--border-subtle)',
          display: 'flex',
          gap: '4px',
          alignItems: 'center',
        }}
      >
        <div style={{ width: '8px', height: '8px', background: '#bbb', borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out both', animationDelay: '0ms' }} />
        <div style={{ width: '8px', height: '8px', background: '#bbb', borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out both', animationDelay: '160ms' }} />
        <div style={{ width: '8px', height: '8px', background: '#bbb', borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out both', animationDelay: '320ms' }} />
      </div>
    </div>
  );
}

function HistoryTray({ sessions, onSelect, onClose }) {
  function formatTimeAgo(isoString) {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return diffMins <= 1 ? 'Just now' : `${diffMins} minutes ago`;
    if (diffHours < 24) return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.3)',
          backdropFilter: 'blur(2px)',
        }}
      />
      <div
        style={{
          position: 'relative',
          width: '85%',
          maxWidth: '320px',
          background: 'white',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideIn 0.2s ease-out',
        }}
      >
        <style>{`
          @keyframes slideIn {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
        `}</style>
        <div
          style={{
            padding: '1.25rem 1rem',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600 }}>Recent Chats</h2>
          <button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: 'var(--cream)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
            }}
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
          {sessions.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '2rem 0' }}>
              No previous chats yet
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {sessions.map((session) => (
                <button
                  key={session.session_id}
                  onClick={() => onSelect(session)}
                  style={{
                    background: 'var(--cream)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: '12px',
                    padding: '0.875rem 1rem',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'transform 0.1s ease, box-shadow 0.1s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <p
                    style={{
                      margin: '0 0 0.375rem',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: 'var(--black)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: 1.4,
                    }}
                  >
                    "{session.preview || 'Chat session'}"
                  </p>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.75rem',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    <span>{formatTimeAgo(session.last_active)}</span>
                    <span>·</span>
                    <span>{session.message_count} messages</span>
                    {session.learned_count > 0 && (
                      <>
                        <span>·</span>
                        <span style={{ color: 'var(--sage)' }}>
                          {session.learned_count} learned
                        </span>
                      </>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
